import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "kv_pwa_dismissed";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    const onInstalled = () => { setShow(false); setDeferred(null); localStorage.setItem(DISMISS_KEY, "1"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    let t;
    if (isIOS()) { t = setTimeout(() => { setIosHint(true); setShow(true); }, 3000); }

    return () => {
      if (t) clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* noop */ }
    setDeferred(null);
    setShow(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  if (!show) return null;

  return (
    <div data-testid="pwa-install-banner" className="fixed z-[90] left-1/2 -translate-x-1/2 bottom-[4.75rem] md:bottom-6 safe-above-nav-lg w-[calc(100%-1.5rem)] max-w-md animate-in slide-in-from-bottom-4 duration-300">
      <div className="flex items-center gap-3 rounded-2xl bg-[#0F1117] border border-white/10 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.9)] p-3 pr-2">
        <span className="w-10 h-10 rounded-xl bg-[#0066FF] flex items-center justify-center shrink-0 shadow-[0_4px_16px_rgba(0,102,255,0.5)]">
          <Download size={19} className="text-white" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm leading-tight">Install KinkVault</p>
          {iosHint ? (
            <p className="text-[#9CA3AF] text-xs mt-0.5 flex items-center gap-1 flex-wrap">Tap <Share size={12} className="inline" /> then “Add to Home Screen”</p>
          ) : (
            <p className="text-[#9CA3AF] text-xs mt-0.5">Add the archive to your home screen — works offline</p>
          )}
        </div>
        {!iosHint && (
          <button data-testid="pwa-install-btn" onClick={install} className="shrink-0 px-4 py-2 rounded-lg bg-[#0066FF] hover:bg-[#0055CC] text-white font-semibold text-sm transition-colors">
            Install
          </button>
        )}
        <button data-testid="pwa-install-dismiss" onClick={dismiss} aria-label="Dismiss" className="shrink-0 w-8 h-8 rounded-full text-[#6B7280] hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
